import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Modal, Tab, TabPane } from 'semantic-ui-react';
import { Button } from '../../../../components/button/Button';
import { MaskedInput } from '../../../../components/maskedInput/MaskedInput';
import { Checkbox } from '../../../../components/checkbox/Checkbox';
import { Icon } from '../../../../components/icon/Icon';
import { axios } from '../../../../services/axiosService';
import {
    loadPaymentInformation,
    loadPaymentProfile,
} from '../billingSettingsActionCreators';
import {
    setAutopayAccount,
    setAutopayCustomerId,
    setAutopayLocationId,
    setAutopayModalOpen,
    useAppStateBillingSettingsSelector,
} from '../billingSettingsSlice';
import styles from './AutopayConfigModal.module.scss';
import { openMessageModal } from '../../../../components/statusModal/messageModalActionCreators';
import { AnyAction } from 'redux';
import { useFeatureFlag } from '../../../featureFlags/FeatureFlag';
import { useAuthSelector } from '../../../auth/authSlice';

interface AutopayConfigModalProps {
    modalOpen: boolean;
    enabled?: boolean;
    savedCard?: string;
    id?: string;
}

export const AutopayConfigModal = ({
    modalOpen,
    enabled: propEnabled,
    savedCard: propSavedCard,
    id: propId,
}: AutopayConfigModalProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [enabled, setEnabled] = useState(propEnabled ?? false);
    const [apiError, setApiError] = useState('');
    // Disables the Save button while a submit is in flight so an impatient
    // double-click can't fire a second /autopay request (which would create a
    // duplicate Salesforce case). Backstop to the server-side 409 guard.
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [savedCard, setSavedCard] = useState(propSavedCard ?? undefined);
    const [id, setId] = useState<string | undefined>();

    const [accountNumber, setAccountNumber] = useState('');
    const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [confirmRoutingNumber, setConfirmRoutingNumber] = useState('');
    const [accountType, setAccountType] = useState('');
    const [mismatchAccountNumber, setMismatchAccountNumber] = useState(false);
    const [mismatchRoutingNumber, setMismatchRoutingNumber] = useState(false);

    const coreFF = useFeatureFlag('EnableCORE');
    const enableAchAutopay = useFeatureFlag('EnableACHAutopay');

    const { authUser } = useAuthSelector((state) => state);
    const {
        paymentProfile,
        autopayLocationId,
        autopayCustomerId,
        coreStoredPayments,
        autopayStatus,
    } = useAppStateBillingSettingsSelector((s) => s);

    const [tab, setTab] = useState(
        autopayStatus ? (autopayStatus === 'C' ? 1 : 0) : 0,
    );

    useEffect(() => {
        setEnabled(propEnabled ?? false);
        setSavedCard(propSavedCard ?? undefined);
        setId(propId);
    }, [propEnabled, propSavedCard, propId]);

    // Reset all state when modal is closed
    const onClose = () => {
        setApiError('');
        setEnabled(false);
        setSavedCard(undefined);
        setId(undefined);
        setAccountNumber('');
        setConfirmAccountNumber('');
        setRoutingNumber('');
        setConfirmRoutingNumber('');
        setAccountType('');
        setMismatchAccountNumber(false);
        setMismatchRoutingNumber(false);
        setTab(0);
        dispatch(setAutopayAccount(null));
        dispatch(setAutopayCustomerId(null));
        dispatch(setAutopayLocationId(null));
        dispatch(setAutopayModalOpen(false));
    };

    const onSubmit = async () => {
        // Re-entry guard: ignore a second click while the first is still in
        // flight (belt-and-suspenders with the disabled button below).
        if (isSubmitting) {
            return;
        }
        setApiError('');
        setIsSubmitting(true);
        // Close the modal only after the request settles, so we never
        // setState on an unmounted component (onClose unmounts the modal).
        let succeeded = false;
        try {
            if (enabled) {
                if (tab === 0) {
                    // Check if Cards are selected
                    if (
                        !savedCard &&
                        ((!coreFF?.enabled && !paymentProfile.length) ||
                            (coreFF?.enabled && !coreStoredPayments.length))
                    ) {
                        setApiError(t('Billing.ConfigureAutopayNoCardsError'));
                        return;
                    } else if (
                        !savedCard &&
                        ((!coreFF?.enabled && paymentProfile.length) ||
                            (coreFF?.enabled && coreStoredPayments.length))
                    ) {
                        setApiError(
                            t('Billing.ConfigureAutopaySelectCardError'),
                        );
                        return;
                    }
                } else {
                    // Check if Bank Account is selected
                    if (!accountNumber || !routingNumber || !accountType) {
                        setApiError(t('Billing.BankAccountFieldsRequired'));
                        return;
                    }

                    var error = false;

                    if (accountNumber !== confirmAccountNumber) {
                        setMismatchAccountNumber(true);
                        error = true;
                    } else {
                        setMismatchAccountNumber(false);
                    }

                    if (routingNumber !== confirmRoutingNumber) {
                        setMismatchRoutingNumber(true);
                        error = true;
                    } else {
                        setMismatchRoutingNumber(false);
                    }

                    if (error) {
                        return;
                    }
                }

                const data = {
                    navilineCustomerId: autopayCustomerId,
                    navilineLocationId: autopayLocationId,
                } as any;

                if (tab === 0) {
                    data.savedCard = savedCard?.toString();
                } else {
                    data.accountNumber = accountNumber;
                    data.routingNumber = routingNumber;
                    data.accountType = accountType;
                }

                await axios.put('/autopay', data);
                dispatch(
                    openMessageModal(
                        'success-update',
                        'success',
                    ) as unknown as AnyAction,
                );
            } else {
                if (id === null) {
                    await axios.delete(`/autopay/`, {
                        data: {
                            accountNumber:
                                autopayCustomerId + '-' + autopayLocationId,
                        },
                    });
                } else {
                    await axios.delete(`/autopay/${id}`);
                }

                dispatch(
                    openMessageModal(
                        'success-update',
                        'success',
                    ) as unknown as AnyAction,
                );
            }

            dispatch(loadPaymentInformation());
            dispatch(loadPaymentProfile(authUser?.Id, coreFF?.enabled));
            succeeded = true;
        } catch (err) {
            console.error(err);

            // Duplicate submit (e.g. an impatient double-click): the API rejects
            // the second request with 409 so it can't create a second Salesforce
            // case. The concurrent first request drives the real outcome, so
            // swallow this silently rather than showing the user an error. Scoped
            // to the ACH tab (tab === 1) — the only path the API 409s — mirroring
            // the tab-scoped 422 handler below.
            if (tab === 1 && (err as any)?.response?.status === 409) {
                return;
            }

            // Handle specific error cases
            if (tab !== 1 && err && (err as any).response?.status === 422) {
                dispatch(
                    openMessageModal(
                        'invalid-routing-number',
                        'error',
                    ) as unknown as AnyAction,
                );
                setApiError(t('Billing.AutopaySaveError'));
                return;
            }

            // Extract error message from response if available
            const errorResponse = (err as any)?.response;
            const errorMessage = errorResponse?.data?.message || errorResponse?.data || null;
            console.error('Error response:', errorResponse);

            // Display specific error message if available from backend
            let modalMessage = t('Billing.AutopaySaveError'); // Default fallback
            
            if (errorMessage && typeof errorMessage === 'string') {
                // Use the specific error message from backend
                setApiError(`${errorMessage}`);
                modalMessage = errorMessage;
            } else {
                // Fall back to generic error
                setApiError(t('Billing.AutopaySaveError'));
            }

            dispatch(
                openMessageModal(
                    modalMessage,
                    'error',
                    {
                        overrideMessageContents: true,
                    },
                ) as unknown as AnyAction,
            );
        } finally {
            setIsSubmitting(false);
        }
        if (succeeded) {
            onClose(); // resets state
        }
        return;
    };

    const bankAccount = () => {
        return (
            <div className={styles.achForm}>
                <p className={styles.securityMessage}>
                    {/* Source web and mobile from one translation row so the
                        admin-editable language rows can't diverge over time.
                        Mobile reads ElginMobile2.AUTOPAY_SECURITY_MESSAGE; web
                        previously read a separate Billing.AchSecurityMessage row,
                        which could drift if either was edited on its own. */}
                    {t('ElginMobile2.AUTOPAY_SECURITY_MESSAGE')}
                </p>

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>
                        {t('Billing.RoutingNumber')}
                    </p>
                    <MaskedInput
                        fluid
                        value={routingNumber}
                        onChange={(e, data) => {
                            setRoutingNumber(data.value);
                        }}
                    />
                </div>

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>
                        {t('Billing.ConfirmRoutingNumber')}
                    </p>
                    <MaskedInput
                        fluid
                        value={confirmRoutingNumber}
                        onChange={(e, data) => {
                            setConfirmRoutingNumber(data.value);
                        }}
                    />
                </div>

                {mismatchRoutingNumber && (
                    <div className={styles.fieldError}>
                        {t('Billing.RoutingNumberMismatch')}
                    </div>
                )}

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>
                        {t('Billing.AccountNumber')}
                    </p>
                    <MaskedInput
                        fluid
                        value={accountNumber}
                        onChange={(e, data) => {
                            setAccountNumber(data.value);
                        }}
                    />
                </div>

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>
                        {t('Billing.ConfirmAccountNumber')}
                    </p>
                    <MaskedInput
                        fluid
                        value={confirmAccountNumber}
                        onChange={(e, data) => {
                            setConfirmAccountNumber(data.value);
                        }}
                    />
                </div>

                {mismatchAccountNumber && (
                    <div className={styles.fieldError}>
                        {t('Billing.AccountNumberMismatch')}
                    </div>
                )}

                <div className={styles.accountTypeGroup}>
                    <Checkbox
                        onChange={(e, d) => {
                            const checked = d.checked;

                            if (checked) {
                                setAccountType('checking');
                            }
                        }}
                        text={t('Billing.AccountTypeChecking')}
                        value={accountType === 'checking'}
                    />
                    <Checkbox
                        onChange={(e, d) => {
                            const checked = d.checked;

                            if (checked) {
                                setAccountType('savings');
                            }
                        }}
                        text={t('Billing.AccountTypeSavings')}
                        value={accountType === 'savings'}
                    />
                </div>
            </div>
        );
    };

    return (
        <Modal open={modalOpen} onClose={onClose} size="small">
            <Modal.Header>
                {t('Billing.ConfigureAutopay')}
                <div style={{ float: 'right' }}>
                    <Icon
                        icon="x"
                        color="black"
                        size="large"
                        onClick={onClose}
                    />
                </div>
            </Modal.Header>
            <Modal.Content>
                <div className={styles.modalContent}>
                    {apiError !== '' && (
                        <p className={styles.errorMessage}>{apiError}</p>
                    )}
                    <div style={{ paddingBottom: '20px' }}>
                        <Checkbox
                            value={enabled}
                            onChange={(_) => setEnabled(!enabled)}
                            text={t('Billing.EnableAutopay')}
                        />
                    </div>

                    {enabled && (
                        <Tab
                            defaultActiveIndex={tab}
                            onTabChange={(e, data) => {
                                if (data.activeIndex) {
                                    setTab(data.activeIndex as number);
                                }
                            }}
                            panes={[
                                {
                                    menuItem: 'Cards',
                                    render: () => (
                                        <TabPane>
                                            {(!coreFF?.enabled &&
                                                paymentProfile.length) ||
                                            (coreFF?.enabled &&
                                                coreStoredPayments &&
                                                coreStoredPayments.length) ? (
                                                <div
                                                    className={
                                                        styles.formContainer
                                                    }
                                                >
                                                    <div>
                                                        {t(
                                                            'Billing.SelectCard',
                                                        )}
                                                        :
                                                    </div>
                                                    <div>
                                                        {coreFF?.enabled
                                                            ? coreStoredPayments.map(
                                                                  (x: any) => (
                                                                      <div
                                                                          className={
                                                                              styles.radioButtonContainer
                                                                          }
                                                                      >
                                                                          <Checkbox
                                                                              key={
                                                                                  x.id
                                                                              }
                                                                              text={`${x.card_type} - ${x.accountnum}`}
                                                                              value={
                                                                                  savedCard &&
                                                                                  x &&
                                                                                  Number(
                                                                                      savedCard,
                                                                                  ) ===
                                                                                      Number(
                                                                                          x.id,
                                                                                      )
                                                                              }
                                                                              onChange={() => {
                                                                                  setSavedCard(
                                                                                      x.id,
                                                                                  );
                                                                              }}
                                                                          />
                                                                      </div>
                                                                  ),
                                                              )
                                                            : paymentProfile.map(
                                                                  (x: any) => (
                                                                      <Checkbox
                                                                          key={
                                                                              x.Id
                                                                          }
                                                                          text={`${x.Type} - ${x.MaskedAccountNumber}`}
                                                                          value={
                                                                              savedCard ===
                                                                              x.Id
                                                                          }
                                                                          onChange={() =>
                                                                              setSavedCard(
                                                                                  x.Id,
                                                                              )
                                                                          }
                                                                      />
                                                                  ),
                                                              )}
                                                    </div>
                                                </div>
                                            ) : enabled &&
                                              ((!coreFF?.enabled &&
                                                  !paymentProfile.length) ||
                                                  (coreFF?.enabled &&
                                                      coreStoredPayments &&
                                                      !coreStoredPayments.length)) ? (
                                                <>
                                                    <div
                                                        className={
                                                            styles.errorMessage
                                                        }
                                                    >
                                                        {t(
                                                            'Billing.NoSavedCards',
                                                        )}
                                                    </div>
                                                </>
                                            ) : null}
                                        </TabPane>
                                    ),
                                },
                                ...(enableAchAutopay?.enabled
                                    ? [
                                          {
                                              menuItem: 'Bank Account',
                                              render: bankAccount,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    )}

                    <div className={styles.buttonWrapper}>
                        {autopayStatus && autopayStatus === 'C' && (
                            <p className={styles.warningMessage}>
                                {t('Billing.AchAlreadyConfiguredWarning')}
                            </p>
                        )}
                        <Button
                            onClick={() => onSubmit()}
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            {t('Util.Save')}
                        </Button>
                    </div>
                </div>
            </Modal.Content>
        </Modal>
    );
};
